import { useMemo, useState } from 'react'
import { MessageSquare, Plus, Search, Trash2 } from 'lucide-react'
import type { ChatSessionSummary } from '~/api/types'
import { cn } from '~/lib/utils'
import { formatDate } from '~/lib/date-utils'

export interface ChatHistorySidebarProps {
    /** 상담 세션 목록(updatedAt 내림차순). */
    sessions: ChatSessionSummary[]
    /** 현재 선택된 세션 id. 없으면 새 상담 상태. */
    activeSessionId: string | null
    /** 세션 클릭 시 호출. */
    onSelect(session: ChatSessionSummary): void
    /** "새 상담" 클릭 시 호출. */
    onNew(): void
    /** 세션 삭제 확정 시 호출(사이드바가 확인창을 띄운 뒤 호출한다). */
    onDelete?(session: ChatSessionSummary): void
    /** 로그인 여부. 비로그인 시 안내문을 보여준다. */
    isAuthenticated: boolean
    /** 목록 로딩 중 여부. */
    loading?: boolean
}

/** 계정별 상담 히스토리 좌측 사이드바. */
export function ChatHistorySidebar({
    sessions,
    activeSessionId,
    onSelect,
    onNew,
    onDelete,
    isAuthenticated,
    loading,
}: ChatHistorySidebarProps) {
    const [query, setQuery] = useState('')

    // 입력값으로 세션 title을 필터(대소문자 무시·부분일치). 비어있으면 전체.
    const filteredSessions = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return sessions
        return sessions.filter((s) => (s.title ?? '').toLowerCase().includes(q))
    }, [sessions, query])

    return (
        <aside className='border-border bg-card hidden w-72 shrink-0 flex-col rounded-md border lg:flex'>
            <div className='border-border border-b p-3'>
                <button
                    type='button'
                    onClick={onNew}
                    className='border-border bg-background text-foreground hover:border-primary hover:text-primary flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors'>
                    <Plus className='size-4' />새 상담
                </button>

                {isAuthenticated ? (
                    <div className='relative mt-2'>
                        <Search className='text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2' />
                        <input
                            type='search'
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder='상담 검색'
                            aria-label='상담 검색'
                            className='border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary w-full rounded-md border py-1.5 pr-2.5 pl-8 text-sm transition-colors outline-none'
                        />
                    </div>
                ) : null}
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto p-2'>
                {!isAuthenticated ? (
                    <p className='text-muted-foreground px-2 py-6 text-center text-sm'>
                        로그인하시면 지난 상담 내역을 볼 수 있어요.
                    </p>
                ) : loading ? (
                    <p className='text-muted-foreground px-2 py-6 text-center text-sm'>
                        상담 내역을 불러오는 중이에요…
                    </p>
                ) : sessions.length === 0 ? (
                    <p className='text-muted-foreground px-2 py-6 text-center text-sm'>
                        아직 상담 내역이 없어요. 새 상담을 시작해 보세요.
                    </p>
                ) : filteredSessions.length === 0 ? (
                    <p className='text-muted-foreground px-2 py-6 text-center text-sm'>
                        검색 결과가 없어요.
                    </p>
                ) : (
                    <ul className='space-y-1'>
                        {filteredSessions.map((session) => {
                            const isActive = session.id === activeSessionId
                            return (
                                <li
                                    key={session.id}
                                    className='group/item relative'>
                                    <button
                                        type='button'
                                        onClick={() => onSelect(session)}
                                        aria-current={
                                            isActive ? 'true' : undefined
                                        }
                                        className={cn(
                                            'flex w-full items-start gap-2 rounded-md py-2 pr-9 pl-2.5 text-left text-sm transition-colors',
                                            isActive
                                                ? 'bg-accent text-primary'
                                                : 'text-foreground hover:bg-accent'
                                        )}>
                                        <MessageSquare className='mt-0.5 size-4 shrink-0' />
                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate font-medium'>
                                                {session.title || '새 상담'}
                                            </span>
                                            <span className='text-muted-foreground block text-xs'>
                                                {formatDate(session.updatedAt)}
                                            </span>
                                        </span>
                                    </button>
                                    {onDelete ? (
                                        <button
                                            type='button'
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        `"${session.title || '새 상담'}" 대화를 삭제할까요? 삭제하면 되돌릴 수 없어요.`
                                                    )
                                                ) {
                                                    onDelete(session)
                                                }
                                            }}
                                            aria-label={`${session.title || '새 상담'} 삭제`}
                                            title='대화 삭제'
                                            className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:ring-ring absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-md opacity-0 transition-opacity group-focus-within/item:opacity-100 group-hover/item:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none'>
                                            <Trash2 className='size-4' />
                                        </button>
                                    ) : null}
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </aside>
    )
}
